import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Input from "../components/form/Input";
import { toast } from "react-hot-toast";
import JobSeeker from "../api/JobSeeker";
import { DashboardLayout } from "../components/organisms/Layout";
import useFormatPhoneNumber from "../hooks/FormatPhoneNumber";
import { decodeIds } from "../utils";

const AddJobSeeker = () => {
  const { t } = useTranslation();
  const formatPhoneNumber = useFormatPhoneNumber;
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [jobPosition, setJobPosition] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const editMode = location.pathname.search("/job-seekers/edit") !== -1;
  const seekerId = decodeIds(params.id);

  useEffect(() => {
    if (editMode) {
      document.title = "HeyHire Portal-Edit Job Seeker";
      loadSeeker();
    } else {
      document.title = "HeyHire Portal-Add New Job Seeker";
    }
  }, [editMode]);

  const loadSeeker = () => {
    setPageLoading(true);
    JobSeeker.getById(seekerId)
      .then((response) => {
        const _seeker = response.data.data;
        setFirstName(_seeker.first_name);
        setLastName(_seeker.last_name);
        setJobPosition(_seeker.position || "");
        setCompanyName(_seeker.company || "");
        setPhone(_seeker.phone);
        setEmail(_seeker.email);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong-please-try-again"));
        }
        navigate("/job-seekers");
      })
      .finally(() => setPageLoading(false));
  };

  const handleSubmit = () => {
    setLoading(true);
    setValidationErrors(null);

    const body = editMode
      ? {
          phone: phone,
          email: email,
        }
      : {
          first_name: firstName,
          last_name: lastName,
          position: jobPosition,
          company: companyName,
          phone: phone,
          email: email,
        };

    const request = editMode
      ? JobSeeker.update(seekerId, body)
      : JobSeeker.add(body);

    request
      .then(() => {
        toast.success(
          editMode
            ? t("job-seekers.job-seeker-updated")
            : t("job-seekers.job-seeker-added")
        );
        navigate("/job-seekers");
      })
      .catch((error) => {
        if (error.response?.status === 422) {
          setValidationErrors(error.response.data.errors);
          toast.error(t("common.please-check-form-errors"));
        } else if (error.response?.status !== 403) {
          toast.error(t("common.something-went-wrong-please-try-again"));
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <DashboardLayout darmo={true}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between py-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center">
              <Link
                to="/job-seekers"
                className="mr-4 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <i className="far fa-chevron-left text-xl" />
              </Link>
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:tracking-tight">
                {editMode ? "Edit Job Seeker" : "Add Job Seeker"}
              </h2>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              {editMode
                ? "Edit Job Seeker Information"
                : "Add Job Seeker Information"}
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2 relative">
          {pageLoading && (
            <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-xl z-10">
              <div className="flex items-center gap-2">
                <i className="fad fa-circle-notch fa-spin text-2xl text-primary-500" />
                <span className="text-sm font-medium text-gray-900">
                  {t("common.loading")}
                </span>
              </div>
            </div>
          )}
          <div className="px-4 py-6 sm:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <Input
                darmo={true}
                name="first_name"
                label={t("common.first-name")}
                type="text"
                value={firstName}
                placeholder={t("common.first-name")}
                error={validationErrors?.first_name?.[0]}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={loading || editMode}
              />

              <Input
                darmo={true}
                name="last_name"
                label={t("common.last-name")}
                type="text"
                value={lastName}
                placeholder={t("common.last-name")}
                error={validationErrors?.last_name?.[0]}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading || editMode}
              />

              {!editMode && (
                <Input
                  darmo={true}
                  name="job_position"
                  label={t("common.job-position")}
                  type="text"
                  value={jobPosition}
                  placeholder={t("common.job-position")}
                  error={validationErrors?.position?.[0]}
                  onChange={(e) => setJobPosition(e.target.value)}
                  disabled={loading}
                />
              )}

              {!editMode && (
                <Input
                  darmo={true}
                  name="company"
                  label={t("common.company")}
                  type="text"
                  value={companyName}
                  placeholder={t("common.company")}
                  error={validationErrors?.company?.[0]}
                  onChange={(e) => setCompanyName(e.target.value)}
                  disabled={loading}
                />
              )}

              <Input
                darmo={true}
                name="phone"
                label={t("common.phone")}
                type="text"
                value={formatPhoneNumber(phone)}
                placeholder={t("common.phone-example")}
                error={validationErrors?.phone?.[0]}
                onChange={(e) => {
                  if (e.target.value.length < 15) {
                    setPhone(e.target.value);
                  }
                }}
                disabled={loading}
              />

              <Input
                darmo={true}
                name="email"
                label={t("common.email")}
                type="text"
                value={email}
                placeholder={t("common.example-email")}
                error={validationErrors?.email?.[0]}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-x-4 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            <Link
              to="/job-seekers"
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-all"
            >
              {t("common.cancel")}
            </Link>
            <button
              type="submit"
              onClick={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              disabled={loading}
              className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 transition-all disabled:bg-primary-300 disabled:cursor-not-allowed inline-flex items-center"
            >
              {loading && <i className="fad fa-circle-notch fa-spin mr-2" />}
              {t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddJobSeeker;
